import { supabase } from '../config/supabase';

// Add this function to normalize card names
export function standardizeCardName(input: string): string {
  if (!input) return '';
  
  // Convert to lowercase and trim
  let normalized = input.toLowerCase().trim();
  
  // Handle number words and digits
  const numberMap: { [key: string]: string } = {
    'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
    'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
    'first': '1', 'second': '2', 'third': '3', 'fourth': '4', 'fifth': '5',
    'sixth': '6', 'seventh': '7', 'eighth': '8', 'ninth': '9', 'tenth': '10'
  };

  // Replace number words with digits
  Object.entries(numberMap).forEach(([word, digit]) => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    normalized = normalized.replace(regex, digit);
  });

  // Handle common variations
  const variations: { [key: string]: string } = {
    'thejoker': 'the fool',
    'joker': 'the fool',
    'ten of cups': '10 of cups',
    'themoon': 'the moon',
    'moon': 'the moon',
    'judgement': 'judgment',
    'wheel of fortune': 'wheel-of-fortune',
    'high priestess': 'the-high-priestess',
    'hanged man': 'the-hanged-man',
    'the judgement': 'judgment',
    'ace': '1',
    'king': '14',
    'queen': '13',
    'knight': '12',
    'page': '11'
  };

  // Replace variations
  Object.entries(variations).forEach(([variant, standard]) => {
    if (normalized === variant) {
      normalized = standard;
    }
  });

  // Handle "of" in card names (e.g., "2 of cups", "queen of wands")
  const ofMatch = normalized.match(/^(\d+|king|queen|knight|page)\s+of\s+(\w+)$/i);
  if (ofMatch) {
    const [, rank, suit] = ofMatch;
    normalized = `${rank.toLowerCase()} of ${suit.toLowerCase()}`;
  }

  return normalized;
}

export async function getCardData(cardName: string): Promise<any> {
  try {
    const normalized = standardizeCardName(cardName);
    
    // First try exact match
    const { data: exactMatches, error: exactError } = await supabase
      .from('tarot_cards')
      .select('*')
      .eq('orientation', 'upright')
      .eq('card_name', cardName)
      .limit(1);

    if (!exactError && exactMatches && exactMatches.length > 0) {
      return exactMatches[0];
    }

    // Try normalized name
    const { data: normalizedMatches, error: normalizedError } = await supabase
      .from('tarot_cards')
      .select('*')
      .eq('orientation', 'upright')
      .ilike('card_name', `%${normalized}%`)
      .limit(1);

    if (!normalizedError && normalizedMatches && normalizedMatches.length > 0) {
      return normalizedMatches[0];
    }

    // Try slug aliases
    const { data: aliasMatches, error: aliasError } = await supabase
      .from('tarot_cards')
      .select('*')
      .eq('orientation', 'upright')
      .contains('slug_aliases', [normalized])
      .limit(1);

    if (!aliasError && aliasMatches && aliasMatches.length > 0) {
      return aliasMatches[0];
    }

    // Try one more time with just the number and suit
    if (normalized.includes(' of ')) {
      const parts = normalized.split(' of ');
      const number = parts[0];
      const suit = parts[1];
      
      const { data: looseMatches, error: looseError } = await supabase
        .from('tarot_cards')
        .select('*')
        .eq('orientation', 'upright')
        .ilike('card_name', `%${number}%${suit}%`)
        .limit(1);

      if (!looseError && looseMatches && looseMatches.length > 0) {
        return looseMatches[0];
      }
    }

    // If no match is found, return a default object
    return {
      card_name: cardName,
      image_url: null,
      meaning_summary: null,
      arcana_type: null,
      suit: null,
      themes: [],
      shadow_themes: [],
      traditional_imagery: null
    };
  } catch (error) {
    console.error('Error fetching card data:', error);
    return {
      card_name: cardName,
      image_url: null,
      meaning_summary: null,
      arcana_type: null,
      suit: null,
      themes: [],
      shadow_themes: [],
      traditional_imagery: null
    };
  }
}